import { supabase } from "@/lib/supabaseClient";
import { useAuth } from "@/contexts/AuthContext";
import { Product } from "@/types/Products";

export const useProductMerging = (
    selectedProducts: Product[],
    onSuccess: () => void
) => {
    const { session } = useAuth();

    const mergeProducts = async () => {
        if (selectedProducts.length <= 1) return;

        const mergeId = selectedProducts[0].shopify_variant_id;

        try {
            const oldMergeIds = selectedProducts
                .map((product) => product.merge_id)
                .filter(Boolean);

            const { data: relatedProducts } = await supabase
                .from("products")
                .select("shopify_variant_id")
                .in("merge_id", oldMergeIds);

            const allVariantIds = new Set([
                ...selectedProducts.map(
                    (product) => product.shopify_variant_id
                ),
                ...(relatedProducts?.map((p) => p.shopify_variant_id) || []),
            ]);

            const updates = Array.from(allVariantIds).map((variantId) => ({
                shopify_variant_id: variantId,
                merge_id: mergeId,
                user_id: session?.user.id,
            }));

            const { error } = await supabase.from("products").upsert(updates);

            if (error) throw error;
            onSuccess();
        } catch (error) {
            console.error("Error merging products:", error);
        }
    };

    const unmergeProducts = async () => {
        if (selectedProducts.length === 0) return;

        try {
            const mergeIds = selectedProducts
                .map((product) => product.merge_id)
                .filter(Boolean);

            const { error } = await supabase
                .from("products")
                .update({ merge_id: null })
                .in("merge_id", mergeIds);

            if (error) throw error;
            onSuccess();
        } catch (error) {
            console.error("Error unmerging products:", error);
        }
    };

    const autoMergeVariants = async () => {
        if (selectedProducts.length === 0) return;

        try {
            const selectedProductIds = [
                ...new Set(
                    selectedProducts.map(
                        (product) => product.shopify_product_id
                    )
                ),
            ];

            for (const productId of selectedProductIds) {
                const allVariants = selectedProducts.filter(
                    (product) => product.shopify_product_id === productId
                );

                if (allVariants.length > 0) {
                    const mergeId = allVariants[0].shopify_variant_id;

                    const updates = allVariants.map((variant) => ({
                        shopify_variant_id: variant.shopify_variant_id,
                        merge_id: mergeId,
                        user_id: session?.user.id,
                    }));

                    const { error } = await supabase
                        .from("products")
                        .upsert(updates);
                    if (error) throw error;
                }
            }
            onSuccess();
        } catch (error) {
            console.error("Error auto-merging variants:", error);
        }
    };

    return {
        mergeProducts,
        unmergeProducts,
        autoMergeVariants,
    };
};
